import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { FIELD_TYPES } from "common/constants/enums";

import { setNodeToEdit } from "store/slices/nodes";
import { useAppDispatch, useAppSelector } from "common/hooks";
import CheckCircle from "assets/icons/CheckCircle";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { VisibilityOffOutlined } from "@mui/icons-material";

const QuestionField = ({ node }: any) => {
  const {
    nodeToEdit,
    json: { nodes },
  } = useAppSelector((state) => state.nodes);
  const dispatch = useAppDispatch();
  const parentNode = nodes.find((n: any) => n.id === node.parentNode);
  const required = !!node?.data?.required;

  const defaultValue = node?.data?.properties?.defaultValue?.ifValue ?? "";
  const placeholder = node?.data?.properties?.properties?.placeholder;
  const disabled = !node?.data?.properties?.editable?.condition;
  const isDirectionRow = node?.data?.style?.direction === "row";
  const values = node?.data?.properties?.properties?.values;
  const defaultProps: any = { required, defaultValue, placeholder, disabled, size: "small" };
  const isTypeMultiselect = node?.data?.properties?.type === FIELD_TYPES.multiselect;
  const isTypeText = node?.data?.properties?.type === FIELD_TYPES.text;

  const renderField = () => {
    switch (node?.data?.properties?.type) {
      case FIELD_TYPES.textbox:
        return (
          <TextField
            {...{
              ...defaultProps,
              hiddenLabel: true,
              fullWidth: true,
            }}
          />
        );

      case FIELD_TYPES.multitextbox:
        return (
          <TextField
            {...{
              ...defaultProps,
              hiddenLabel: true,
              fullWidth: true,
            }}
            rows={4}
            multiline
          />
        );
      case FIELD_TYPES.boolean:
        return (
          <RadioGroup
            {...defaultProps}
            aria-labelledby="demo-radio-buttons-group-label"
            name="radio-buttons-group"
            sx={{
              width: isDirectionRow ? "max-content" : 1 / 1,
              maxWidth: isDirectionRow ? "none" : "300px",
            }}
            className={`button-themed-radio-group ${isDirectionRow ? "isDirectionRow" : ""}`}
          >
            {values?.map((val: any, i: any) => (
              <FormControlLabel
                key={i}
                className={`button-themed-radio-label ${isDirectionRow ? "isDirectionRow" : ""}`}
                value={val?.value}
                control={<Radio sx={{ display: "none" }} />}
                label={val?.key}
              />
            ))}
          </RadioGroup>
        );
      case FIELD_TYPES.dropdown:
        return (
          <FormControl fullWidth {...defaultProps}>
            <Select value={defaultValue}>
              <MenuItem value="" sx={{ display: "none" }}></MenuItem>
              {values?.map((val: any, i: any) => (
                <MenuItem value={val?.value} key={i}>
                  {val?.key}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      case FIELD_TYPES.multiselect:
        return (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 2,
              width: "100%",
              maxWidth: "420px",
              justifyContent: "center",
            }}
          >
            {values?.map((val: any, i: any) => (
              <label className="multiselect-box" key={i}>
                <Checkbox sx={{ display: "none" }} />
                <Box
                  sx={{
                    width: 200,
                    minHeight: 160,
                    height: 1 / 1,
                    background: "#fff",
                    textAlign: "center",
                    boxShadow: "2px 2px 4px #ddd",
                    borderRadius: "20px",
                    border: "1px solid rgb(229, 231, 235)",
                    padding: "12px",
                  }}
                >
                  <CheckCircle />
                  <Typography variant="subtitle2" sx={{ mt: 2 }}>
                    {val.key}
                  </Typography>
                </Box>
              </label>
            ))}
          </Box>
        );
      case FIELD_TYPES.text:
        return "";
      case FIELD_TYPES.datetime:
        return (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              maxDate={dayjs()}
              slotProps={{ textField: { size: "small", hiddenLabel: true, fullWidth: true } }}
            />
          </LocalizationProvider>
        );
      default:
        return (
          <Typography variant="button" color="warning">
            {node?.data?.properties?.type}
          </Typography>
        );
    }
  };
  return (
    <Box
      onClick={(e) => {
        e.stopPropagation();
        dispatch(setNodeToEdit(node));
      }}
      sx={{
        display: "flex",
        justifyContent:
          (!(parentNode?.data?.boxLayout === "TRUE") && parentNode?.data?.style?.justify) ||
          "center",
        borderRadius: 1,

        backgroundColor: nodeToEdit?.id === node?.id ? "#1976d215 !important" : "",
      }}
    >
      <Box
        sx={{
          my: isTypeText ? 0 : 3,
          maxWidth: isTypeMultiselect
            ? "70%"
            : isTypeText
            ? "100%"
            : isDirectionRow
            ? "85%"
            : "300px",
          width: 1 / 1,
          display: "flex",
          alignItems: isDirectionRow ? "center" : "flex-start",
          flexDirection: isDirectionRow ? "row" : "column",
          justifyContent: isDirectionRow ? "space-between" : "center",
        }}
      >
        <InputLabel
          sx={{
            whiteSpace: "pre-wrap",
            ...(isTypeMultiselect
              ? { mx: "auto", fontSize: "20px", color: "#000", fontWeight: 600, mb: 2 }
              : isTypeText
              ? {
                  fontSize: "16px",
                  color: "#000000DE",
                  mx: "auto",
                  fontWeight: 500,
                  textAlign: "center",
                }
              : {}),
          }}
        >
          {node?.data?.properties?.title?.ifValue || node?.data?.properties?.title?.elseValue}{" "}
          {!isTypeText ? (node?.data?.required?.condition ? "*" : "(Optional)") : ""}
        </InputLabel>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            width: isDirectionRow ? "auto" : 1 / 1,
            justifyContent: isTypeMultiselect || isTypeText ? "center" : "flex-start",
            position: "relative",
          }}
        >
          {renderField()}
          {!node?.data?.visibility?.condition && (
            <VisibilityOffOutlined
              sx={{
                ml: 1,
                fontSize: "14px",
                color: "#d32f2f",
              }}
            />
          )}
          {node?.data?.style?.row && (
            <Typography
              variant="caption"
              color="info"
              sx={{ whiteSpace: "nowrap", position: "absolute", left: "calc(100% + 5px)" }}
            >
              (Row: <b>{node?.data?.style?.row}</b>)
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default QuestionField;
